"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Target, Calculator, Zap, Compass, BookOpen, Telescope, Users } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function MidpointsPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-blue-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-blue-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-blue-500">/</span>
                <span className="text-sm">Orta Noktalar</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Orta Noktalar (Midpoints)</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-blue-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Target className="h-10 w-10 text-blue-400" />
                Orta Noktalar (Midpoints)
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                İki gezegen veya hassas nokta arasındaki matematiksel orta noktanın hesaplanması ve yorumlanması.
                Hamburg Okulu ve Uranian Astroloji'nin temel tekniklerinden biri olan bu yöntem,
                gizli bağlantıları ve hassas etkileri ortaya çıkarır.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavram</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-blue-400" />
                      Hesaplama Yöntemi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Midpoint = (Gezegen A + Gezegen B) ÷ 2
                      </p>
                      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-200 mb-2">Örnek Hesaplama:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Güneş: 15° Koç (15°)</div>
                          <div>Ay: 25° İkizler (85°)</div>
                          <div className="border-t border-blue-700/30 pt-2 mt-2">
                            <strong>Midpoint: (15° + 85°) ÷ 2 = 50° = 20° Boğa</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Target className="h-5 w-5 text-cyan-400" />
                      Sensitive Noktalar
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Midpoint noktasına yapılan transitlerin ve aspectlerin
                        her iki gezegeni de aynı anda etkilediği kabul edilir.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Transit gezegenler midpoint'i tetikler</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Progressed gezegenler de etkilidir</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Genellikle 1-2° orb kullanılır</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Background */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Hamburg School */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Hamburg Okulu (1920'ler)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Alfred Witte tarafından kurulan Hamburg Okulu, midpoint sistemini
                      modern astrolojiye kazandırdı. 8 hipotez gezegen ve 90° dial kullanımı.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Alfred Witte:</strong> Sistemin kurucusu</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Ludwig Rudolph:</strong> Sistemin geliştiricisi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Transneptün gezegenleri:</strong> 8 hipotez gezegen</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modern Development */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Gelişim</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Reinhold Ebertin'in Kozmobiyoloji yaklaşımı ile midpoint sistemi
                      sadeleştirildi ve daha geniş kullanım alanı buldu.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Reinhold Ebertin:</strong> Kozmobiyoloji</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>90° Dial:</strong> Görsel analiz aracı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Midpoint Trees:</strong> Sistemli analiz</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Midpoints */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Midpoint'ler</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Midpoints */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-purple-300">Kişisel Midpoint'ler</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉/☽ (Güneş/Ay)</div>
                      <div className="text-sm text-slate-400">Bilinçli ve bilinçsiz benliğin birleşimi, vitality</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉/♂ (Güneş/Mars)</div>
                      <div className="text-sm text-slate-400">Eylem, motivasyon, erkeksi enerji</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽/♀ (Ay/Venüs)</div>
                      <div className="text-sm text-slate-400">Duygusal güzellik, kadınsi cazibe, sanat</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿/♃ (Merkür/Jüpiter)</div>
                      <div className="text-sm text-slate-400">Genişleyen düşünce, öğretim, yayıncılık</div>
                    </div>
                  </div>
                </div>

                {/* Relationship Midpoints */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-pink-300">İlişki Midpoint'leri</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀/♂ (Venüs/Mars)</div>
                      <div className="text-sm text-slate-400">Aşk, cinsellik, romantik çekim</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽/♀ (Ay/Venüs)</div>
                      <div className="text-sm text-slate-400">Duygusal bağ, şefkat, estetik</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♃/♆ (Jüpiter/Neptün)</div>
                      <div className="text-sm text-slate-400">Ruhsal bağ, idealleşme, empati</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♄/♇ (Satürn/Pluto)</div>
                      <div className="text-sm text-slate-400">Derin dönüşüm, karma bağları</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Uygulama</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Compass className="h-5 w-5 text-teal-400" />
                      90° Dial Kullanımı
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        360°'lik zodiakın 90°'lik parçalara bölünmesi ile
                        tüm hard aspectler (0°, 90°, 180°, 270°) aynı eksende görülür.
                      </p>
                      <div className="bg-teal-900/20 border border-teal-700/50 rounded-lg p-3">
                        <div className="text-sm text-slate-300">
                          <div className="font-medium mb-2">Eşdeğer Pozisyonlar:</div>
                          <div>0° Koç = 0° Yengeç = 0° Terazi = 0° Oğlak</div>
                          <div>15° Koç = 15° Yengeç = 15° Terazi = 15° Oğlak</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-400" />
                      Transit Aktivasyonu
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Transit bir gezegen midpoint'e conjunction, square veya
                        opposition yaptığında, her iki natal gezegeni de aktive eder.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Güçlü etki: Conjunction (0°)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">Orta etki: Square (90°), Opposition (180°)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                          <span className="text-sm text-slate-400">1-2° orb toleransı</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interpretation Examples */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama Örnekleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-indigo-300">☉/♃ Midpoint</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 font-medium">Güneş/Jüpiter Orta Noktası</p>
                    <div className="space-y-2 text-sm">
                      <div className="bg-indigo-900/20 border border-indigo-700/30 rounded-lg p-3">
                        <div className="font-medium text-indigo-200 mb-2">Pozitif Manifestasyon:</div>
                        <div className="text-slate-300">Başarı, genişleme, iyimserlik, liderlik, öğretmenlik yetisi</div>
                      </div>
                      <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3">
                        <div className="font-medium text-red-200 mb-2">Zorlayıcı Aktivasyon:</div>
                        <div className="text-slate-300">Abartı, megalomani, hukuki sorunlar, aşırı güven</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-purple-300">♀/♄ Midpoint</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 font-medium">Venüs/Satürn Orta Noktası</p>
                    <div className="space-y-2 text-sm">
                      <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-3">
                        <div className="font-medium text-purple-200 mb-2">Olumlu İfade:</div>
                        <div className="text-slate-300">Sadakat, kalıcı sevgi, sanatsal disiplin, estetik mükemmellik</div>
                      </div>
                      <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3">
                        <div className="font-medium text-red-200 mb-2">Zor Aktivasyon:</div>
                        <div className="text-slate-300">Duygusal soğukluk, ayrılık, sanatsal blok, kendini değersiz görme</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Combination of Stellar Influences" - Reinhold Ebertin</div>
                    <div>• "Midpoints: Unleashing the Power of the Planets" - Michael Munkasey</div>
                    <div>• "Hamburg School Astrology" - Brummund & Rudolph</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Yaklaşımlar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Dial Detective" - Maria Kay Simms</div>
                    <div>• "Working with Astrology" - Michael Harding & Charles Harvey</div>
                    <div>• "The Astrology of Midpoints" - Don McBroom</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Midpoint astroloji tekniği matematiksel hesaplamalara dayanan ileri düzey bir yöntemdir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel danışmanlık için deneyimli astrologlarla çalışılmalıdır.
              </p>
            </div>

            {/* Main Astrology Training CTA */}
            <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🔭 Astroloji Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Ev sistemleri, natal harita yorumlama, transit analizi, progresyon teknikleri ve gelişmiş astroloji
                  sistemleri hakkında detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Astroloji Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Gelişmiş Sistemler
              </Link>

              <Link
                href="/about/astrology/advanced-systems/harmonics"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Harmonik Astroloji
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}